/**
 * Error Handler Utility
 * Follows: Separation of Concerns, Single Responsibility Principle
 *
 * Purpose: Map backend errors to user-friendly messages
 * Backend defines reality (status codes, error messages)
 * Frontend defines experience (how to present them)
 */

/**
 * Error types based on HTTP status codes
 */
const ErrorType = {
  VALIDATION: "validation", // 400, 409 - expected errors
  UNAUTHORIZED: "unauthorized", // 401
  FORBIDDEN: "forbidden", // 403
  NOT_FOUND: "not_found", // 404
  SERVER: "server", // 500+ - unexpected errors
  NETWORK: "network", // No response
  UNKNOWN: "unknown",
};

/**
 * Determine error type from HTTP response
 */
const getErrorType = (statusCode) => {
  if (!statusCode) return ErrorType.NETWORK;
  if (statusCode === 401) return ErrorType.UNAUTHORIZED;
  if (statusCode === 403) return ErrorType.FORBIDDEN;
  if (statusCode === 404) return ErrorType.NOT_FOUND;
  if (statusCode === 400 || statusCode === 409) return ErrorType.VALIDATION;
  if (statusCode >= 500) return ErrorType.SERVER;
  return ErrorType.UNKNOWN;
};

/**
 * Check if error is a duplicate/conflict error
 */
const isDuplicateError = (errorMessage) => {
  const duplicatePatterns = [
    "already exists",
    "already registered",
    "duplicate",
    "conflict",
    "unique constraint",
  ];

  return duplicatePatterns.some((pattern) =>
    errorMessage.toLowerCase().includes(pattern),
  );
};

/**
 * Parse user operation errors
 * Composition pattern: Build error response from smaller parts
 */
export const parseUserOperationError = (error, operation = "operation") => {
  const statusCode = error.response?.status;
  const backendMessage = error.response?.data?.error || "";
  const errorType = getErrorType(statusCode);

  // Structure: { message, details, type, severity }
  const errorResponse = {
    type: errorType,
    severity: errorType === ErrorType.SERVER ? "high" : "medium",
  };

  // Handle specific error cases
  switch (errorType) {
    case ErrorType.VALIDATION:
      if (isDuplicateError(backendMessage)) {
        errorResponse.message =
          "This email address is already registered in the system.";
        errorResponse.details = "Please use a different email address.";
        errorResponse.actionable = true;
      } else {
        errorResponse.message =
          backendMessage || `Invalid data provided for ${operation}.`;
        errorResponse.details = "Please check the form and try again.";
        errorResponse.actionable = true;
      }
      break;

    case ErrorType.UNAUTHORIZED:
      errorResponse.message = "Your session has expired.";
      errorResponse.details = "Please log in again.";
      errorResponse.severity = "high";
      break;

    case ErrorType.FORBIDDEN:
      errorResponse.message =
        "You do not have permission to perform this action.";
      errorResponse.details =
        "Contact your administrator if you believe this is an error.";
      errorResponse.severity = "high";
      break;

    case ErrorType.NOT_FOUND:
      errorResponse.message = `The ${operation} target could not be found.`;
      errorResponse.details = "It may have been deleted or moved.";
      break;

    case ErrorType.SERVER:
      errorResponse.message = `${operation} failed due to a server error.`;
      errorResponse.details = statusCode
        ? `Server responded with ${statusCode} error. Contact support if this persists.`
        : "Server error occurred. Contact support if this persists.";
      errorResponse.severity = "high";
      break;

    case ErrorType.NETWORK:
      errorResponse.message = "Unable to connect to the server.";
      errorResponse.details = "Check your internet connection and try again.";
      errorResponse.severity = "high";
      break;

    default:
      errorResponse.message =
        backendMessage || `${operation} failed. Please try again.`;
      errorResponse.details = statusCode
        ? `Error ${statusCode}`
        : "Unknown error occurred.";
  }

  return errorResponse;
};

/**
 * Specialized error parsers for specific operations
 * Composition: Build from base parser with operation context
 */
export const parseUserCreationError = (error) => {
  return parseUserOperationError(error, "User creation");
};

export const parseUserUpdateError = (error) => {
  return parseUserOperationError(error, "User update");
};

export const parseUserDeletionError = (error) => {
  return parseUserOperationError(error, "User deletion");
};

export const parseUserApprovalError = (error) => {
  return parseUserOperationError(error, "User approval");
};

export const parseUserRejectionError = (error) => {
  return parseUserOperationError(error, "User rejection");
};

const errorHandler = {
  parseUserCreationError,
  parseUserUpdateError,
  parseUserDeletionError,
  parseUserApprovalError,
  parseUserRejectionError,
  ErrorType,
};

export default errorHandler;
